/**
 * Copyright Highway9 Networks Inc.
 */
import React, { useCallback, useMemo, useState } from 'react';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DownloadIcon from '@mui/icons-material/Download';
import { useHistory } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '~/store/hooks';
import { notificationActions, selectAllNotifications } from '~/store/context/notificationsSlice';
import { notificationPanelStyles } from './styles';
import { NOTIFICATIONS } from '~/constants/routes';
import { Notification, NotificationStatus } from '~/types/Notification';
import moment from 'moment';
import { operationService } from '~/services';
import { enqueueSnackbar } from 'notistack';
import { formatRelativeTime, formatTimestamp, getNotificationStatusIcon, setNotificationPanelLastOpenedTime, getDownloadUrlFromOutput, formatCompletedIn } from './utils';
import { Box, Button, Divider, IconButton, List, ListItem, ListItemText, ListItemIcon, ListSubheader, Popover, Stack, Switch, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@h9/ui-lib';
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

const ACTIVE_STATUSES: NotificationStatus[] = ['queued', 'in-progress'];
const COMPLETED_STATUSES: NotificationStatus[] = ['completed', 'cancelled'];

interface NotificationPanelProps {
  onClose: () => void;
  anchorEl: HTMLElement | null;
}

/**
 * NotificationPanel Component
 * Displays recent notifications in a minimal popover
 */
export const NotificationPanel: React.FC<NotificationPanelProps> = ({ onClose, anchorEl }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const recentNotifications = useAppSelector(selectAllNotifications);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'failed'>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  // Save timestamp when panel is closed
  React.useEffect(() => {
    return () => {
      console.log('NotificationPanel closed');
      setNotificationPanelLastOpenedTime();
      dispatch(notificationActions.markAllAsRead());
    };
  }, [dispatch]);

  const handleStatusFilterChange = (_: React.MouseEvent<HTMLElement>, value: typeof statusFilter | null) => {
    if (value) {
      setStatusFilter(value);
    }
  };

  const handleViewAllOperations = () => {
    history.push(NOTIFICATIONS);
    onClose();
  };

  const handleNotificationClick = useCallback((notification: Notification) => {
    dispatch(notificationActions.markAsRead(notification.id));
    history.push(NOTIFICATIONS + '&operationId=' + notification.operationId);
    onClose();
  }, [dispatch, history, onClose]);

  const filteredNotifications = useMemo(() => {
    return recentNotifications.filter((notification) => {
      if (showUnreadOnly && notification.read) {
        return false;
      }

      if (statusFilter === 'active') {
        return ACTIVE_STATUSES.includes(notification.status);
      }

      if (statusFilter === 'completed') {
        return COMPLETED_STATUSES.includes(notification.status);
      }

      if (statusFilter === 'failed') {
        return notification.status === 'failed';
      }

      return true;
    });
  }, [recentNotifications, statusFilter, showUnreadOnly]);

  const groupedNotifications = useMemo(() => {
    const groups = new Map<string, { label: string; items: Notification[]; sortKey: number }>();

    filteredNotifications.forEach((notification) => {
      const timestampMoment = notification.timestamp > 1e12
        ? moment(notification.timestamp)
        : moment.unix(notification.timestamp);

      const dateKey = timestampMoment.format('YYYY-MM-DD');
      const isToday = timestampMoment.isSame(moment(), 'day');
      const isYesterday = timestampMoment.isSame(moment().subtract(1, 'day'), 'day');
      const label = isToday
        ? 'Today'
        : isYesterday
          ? 'Yesterday'
          : timestampMoment.format('MMMM D, YYYY');

      if (!groups.has(dateKey)) {
        groups.set(dateKey, { label, items: [], sortKey: timestampMoment.startOf('day').valueOf() });
      }

      groups.get(dateKey)!.items.push(notification);
    });

    return Array.from(groups.values())
      .sort((a, b) => b.sortKey - a.sortKey)
      .map((group) => ({
        ...group,
        items: group.items.sort((a, b) => b.timestamp - a.timestamp),
      }));
  }, [filteredNotifications]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={notificationPanelStyles.popover}
    >
      <Box sx={notificationPanelStyles.content}>
        {/* Header */}
        <Box sx={notificationPanelStyles.header}>
          <Typography sx={notificationPanelStyles.title}>
            Recent Activity
          </Typography>
        </Box>

        {/* Filter Bar */}
        <Box sx={notificationPanelStyles.filterBar}>
          <ToggleButtonGroup sx={{ height : 30}}
            value={statusFilter}
            exclusive
            onChange={handleStatusFilterChange}
            size="small"
          >
            {STATUS_FILTERS.map((filter) => (
              <ToggleButton key={filter.value} value={filter.value}>
                {filter.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <Stack direction="row" alignItems="center">
          <Switch size="small"
              checked={showUnreadOnly}
              onChange={() => setShowUnreadOnly((prev) => !prev)}
              label="Unread"
            />
            <Tooltip title="Mark all as read">
              <IconButton size="small" onClick={() => dispatch(notificationActions.markAllAsRead())}>
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Notification List */}
        <Box sx={notificationPanelStyles.listContainer}>
          {groupedNotifications.length === 0 ? (
            <Box sx={notificationPanelStyles.emptyState}>
              <NotificationsNoneIcon sx={notificationPanelStyles.emptyIcon} />
              <Typography variant="h6" color="text.secondary">
                No active operations
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Operations and notifications will appear here
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {groupedNotifications.map((group) => (
                <React.Fragment key={group.sortKey}>
                  <ListSubheader component="div" sx={notificationPanelStyles.listSubheader}>
                    {group.label}
                  </ListSubheader>
                  {group.items.map((notification) => (
                    <NotificationItem key={notification.id} notification={notification} handleClick={handleNotificationClick} />
                  ))}
                </React.Fragment>
              ))}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={notificationPanelStyles.footer}>
          {/* {recentNotifications.length > 0 && (
            <Button
              variant="text"
              size="small"
              onClick={handleClearAll}
              sx={{ flex: 1 }}
            >
              Clear All
            </Button>
          )} */}
          <Button
            variant="text"
            size="small"
            onClick={handleViewAllOperations}
            sx={{ flex: 1 }}
          >
            View All Operations
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default NotificationPanel;

type NotificationItemProps = {
  notification: Notification;
  handleClick: (notification: Notification) => void;
}

function NotificationItem({ notification, handleClick }: NotificationItemProps) {
  const dispatch = useAppDispatch();
  
  // const handleDismiss = useCallback((id: string) => {
  //   dispatch(notificationActions.removeNotification(id));
  // }, [dispatch]);

  const handleDownload = useCallback(async (notification: Notification) => {
    const url = getDownloadUrlFromOutput(notification.metadata?.output);
    if (!url) {
      enqueueSnackbar('No downloadable content found for this notification.', { variant: 'warning' });
      return;
    }

    try {
      enqueueSnackbar('Preparing download...');
      const success = await operationService.DownloadFile(url);
      if (success) {
        enqueueSnackbar('Download completed', { variant: 'success' });
      } else {
        enqueueSnackbar('Download failed', { variant: 'error' });
      }
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(`Download error: ${(error as Error).message}`, { variant: 'error' });
    }
  }, []);

  const renderNotificationActions = useCallback((notification: Notification) => {
    const actions: React.ReactNode[] = [];

    if (notification.actions?.length) {
      notification.actions.forEach((action, index) => {
        actions.push(
          <Button
            key={`custom-action-${notification.id}-${index}`}
            size="small"
            variant={action.variant ?? 'outlined'}
            color={action.color ?? 'primary'}
            onClick={(event) => {
              event.stopPropagation();
              dispatch(notificationActions.markAsRead(notification.id));
              action.handler();
            }}
            disabled={action.disabled}
          >
            {action.label}
          </Button>
        );
      });
    }

    const downloadUrl = getDownloadUrlFromOutput(notification.metadata?.output);
    if (downloadUrl) {
      actions.push(
        <Tooltip title="Download">
        <IconButton
          key={`download-${notification.id}`}
          size="small"
          aria-label="Download"
          onClick={(event) => {
            event.stopPropagation();
            handleDownload(notification);
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
        </Tooltip>
      );
    }

    return (
      <Stack direction="row" spacing={1} sx={notificationPanelStyles.actionButtonsContainer}>
        {actions}
      </Stack>
    );
  }, [dispatch, handleDownload]);

  return (
    <ListItem
    key={notification.id}
    sx={notificationPanelStyles.listItem}
    onClick={() => handleClick(notification)}
    alignItems="flex-start"
  >
    <ListItemIcon sx={notificationPanelStyles.listItemIcon}>
      {getNotificationStatusIcon(notification.status)}
    </ListItemIcon>
    <ListItemText
      primary={
        <Box sx={notificationPanelStyles.notificationHeader}>
          <Typography
            variant="subtitle2"
            fontWeight={notification.read ? 500 : 700}
            sx={{ flexGrow: 1 }}
            noWrap
          >
            {notification.title}
          </Typography>
       
        </Box>
      }
      secondary={
        <Box sx={notificationPanelStyles.notificationBody}>
          {notification.subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {notification.subtitle}
            </Typography>
          )}
          <Typography variant="caption" color="text.disabled">
            {formatTimestamp(notification.timestamp)} • {formatRelativeTime(notification.timestamp)}
            {notification.completedIn !== undefined && (
              <> • Took {formatCompletedIn(notification.completedIn)}</>
            )}
          </Typography>
        </Box>
      }
    />
    

    <Stack 
      direction="row" 
      spacing={0.5} 
      alignItems="center"
    >
            {renderNotificationActions(notification)}
      {!notification.read && <Tooltip title="Mark as read">
        <IconButton
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            dispatch(notificationActions.markAsRead(notification.id));
          }}
        >
          <MarkEmailReadIcon fontSize="small" />
        </IconButton>
      </Tooltip>}
      {/* {notification.dismissible && (
        <Tooltip title="Dismiss">
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              handleDismiss(notification.id);
            }}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )} */}
    </Stack>
  </ListItem>
  )
}